import React, { createContext, useState, useEffect, useContext } from 'react';
import { auth, db } from '../firebase';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import licenseService from '../services/licenseService';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [userRole, setUserRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [licenseStatus, setLicenseStatus] = useState(null);
  const [licenseError, setLicenseError] = useState(null);

  const login = async (email, password) => {
    // 1. Verify license first
    const license = await licenseService.verifyLicense();

    if (!licenseService.isLicenseValid(license)) {
      setLicenseStatus(license);
      setLicenseError(license?.message || 'License verification failed');
      throw new Error(license?.message || 'License verification failed');
    }

    setLicenseStatus(license);
    setLicenseError(null);

    // 2. Then proceed with Firebase authentication
    return signInWithEmailAndPassword(auth, email, password);
  };

  const logout = () => {
    return signOut(auth);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);

      if (user) {
        // Check license periodically (once per day)
        if (licenseService.shouldCheckLicense()) {
          try {
            const license = await licenseService.verifyLicense();
            setLicenseStatus(license);

            if (!licenseService.isLicenseValid(license)) {
              setLicenseError(license?.message || 'License expired');
              // Sign out user if license is invalid
              await signOut(auth);
              setLoading(false);
              return;
            }
          } catch (error) {
            console.error('Error verifying license:', error);
            // Continue with cached license if network error
          }
        }

        // Fetch user role from Firestore
        try {
          const userDoc = await getDoc(doc(db, 'users', user.uid));
          if (userDoc.exists()) {
            setUserRole(userDoc.data().role || 'customer'); // Default to customer if no role
          } else {
            setUserRole('customer'); // Default role
          }
        } catch (error) {
          console.error('Error fetching user role:', error);
          setUserRole('customer'); // Default role on error
        }
      } else {
        setUserRole(null);
      }

      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userRole,
    licenseStatus,
    licenseError,
    login,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};
